import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  ArrowForward,
  ElectricMeter,
  ErrorOutline,
} from '@mui/icons-material';
import type { Theme } from '@mui/material/styles';
import type { ConsumptionReminderAlert } from '@/helpers/consumptionReminderAlerts';

const alertStyleMap = {
  OVERDUE: {
    icon: ErrorOutline,
    iconColor: 'error.main',
    borderColor: (theme: Theme) => alpha(theme.palette.error.main, 0.35),
    background: (theme: Theme) => alpha(theme.palette.error.main, 0.05),
    titleColor: 'error.dark',
  },
} as const;

interface ConsumptionReminderAlertCardProps {
  alert: ConsumptionReminderAlert;
  showBuildingName?: boolean;
}

const ConsumptionReminderAlertCard = ({
  alert,
  showBuildingName = false,
}: ConsumptionReminderAlertCardProps) => {
  const styles = alertStyleMap[alert.alertType];
  const SeverityIcon = styles.icon;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: styles.borderColor,
        background: styles.background,
        borderRadius: 3,
        p: 2,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <SeverityIcon sx={{ color: styles.iconColor, mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ color: styles.titleColor, fontWeight: 700 }}>
            {alert.title}
          </Typography>

          {showBuildingName && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
              Objekt: {alert.buildingName}
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {alert.message}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
            <ElectricMeter sx={{ color: 'text.secondary', fontSize: 18 }} />
            <Typography variant="caption" color="text.secondary">
              Odběrné místo: {alert.collectionPointName}
            </Typography>
          </Stack>

          <Button
            component={RouterLink}
            to={alert.actionPath}
            size="small"
            endIcon={<ArrowForward fontSize="small" />}
            sx={{ px: 0, minWidth: 0, textTransform: 'none', fontWeight: 700 }}
          >
            Nahrát odečet
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default ConsumptionReminderAlertCard;
